import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { APIService } from "../../Service";
import {
  CustomSearchInput,
  CustomSelectMenu,
} from "../../Components/FormElements";
import { CustomTab, Loader } from "../Other";
import { PrimaryButton } from "../Cta";

const isFundObject = (fund) =>
  fund &&
  typeof fund === "object" &&
  (fund.asset_class || fund.mutual_fund_id || fund.fund_id);

export default function SelectFund({
  categories = [],
  allowedSelection = 1,
  selectedFunds = [],
  onSelectionChange = () => {},
}) {
  const [activeCategory, setActiveCategory] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [searchedFunds, setSearchedFunds] = useState([]);
  const [isLoadingFunds, setIsLoadingFunds] = useState(false);
  const isInternalUpdate = useRef(false);
  const fundsRequestId = useRef(0);

  // category-wise selections: { [categoryId]: [fund1, fund2] }
  const [selectedFundMap, setSelectedFundMap] = useState({});

  const normalizeFund = (fund, assetClass) => ({
    ...fund,
    fund_id: fund.fund_id ?? fund.mutual_fund_id,
    fund_name: fund.fund_name ?? fund.mutual_fund_name,
    fund_image: fund.fund_image ?? fund.amc_image,
    asset_class: fund.asset_class ?? assetClass,
  });

  const flattenSelectedFunds = (selectionMap) => {
    return Object.values(selectionMap)
      .flat()
      .map((fund) => ({
        ...fund,
        mutual_fund_id: fund.mutual_fund_id ?? fund.fund_id,
        mutual_fund_name: fund.mutual_fund_name ?? fund.fund_name,
        amc_image: fund.amc_image ?? fund.fund_image,
      }))
      .filter((fund) => fund.fund_id || fund.mutual_fund_id);
  };

  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      setActiveCategory(categories[0]);
    }
  }, [categories, activeCategory]);
  useEffect(() => {
    if (!categories.length || !selectedFunds.length) return;
    if (!selectedFunds.every(isFundObject)) return;
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    const grouped = {};

    selectedFunds.forEach((fund) => {
      const category = categories.find(
        (c) => c.label?.toLowerCase() === fund.asset_class?.toLowerCase(),
      );

      if (!category) return;

      if (!grouped[category.id]) {
        grouped[category.id] = [];
      }

      grouped[category.id].push(normalizeFund(fund));
    });

    setSelectedFundMap(grouped);
  }, [categories, selectedFunds]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchValue) {
        getFunds();
      } else {
        fundsRequestId.current += 1;
        setSearchedFunds([]);
        setIsLoadingFunds(false);
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, activeCategory]);

  const getFunds = () => {
    if (!activeCategory) return;

    const requestId = ++fundsRequestId.current;
    setIsLoadingFunds(true);

    APIService(true, "explore_funds/search-fund", {
      query: searchValue,
      asset_class: activeCategory?.label,
    })
      .then((response) => {
        if (requestId !== fundsRequestId.current) return;

        if (response.status_code === 200) {
          const funds = response.data.funds || [];
          setSearchedFunds(
            funds.map((fund) => normalizeFund(fund, activeCategory?.label)),
          );
        }
      })
      .catch((err) => console.log(err))
      .finally(() => {
        if (requestId === fundsRequestId.current) {
          setIsLoadingFunds(false);
        }
      });
  };

  const handleSelection = (funds) => {
    if (!activeCategory) return;

    const currentCategoryId = activeCategory.id;
    const selectedArray = (
      Array.isArray(funds) ? funds : funds ? [funds] : []
    ).map((fund) => normalizeFund(fund, activeCategory.label));

    const existing = selectedFundMap[currentCategoryId] || [];
    const searchIds = new Set(searchedFunds.map((f) => String(f.fund_id)));
    const preserved = existing.filter(
      (fund) => !searchIds.has(String(fund.fund_id)),
    );

    const mergedMap = new Map();
    [...preserved, ...selectedArray].forEach((fund) => {
      mergedMap.set(String(fund.fund_id), fund);
    });
    const merged = Array.from(mergedMap.values());

    if (merged.length > allowedSelection) {
      toast.error(`You can select only ${allowedSelection} fund(s).`);
      return;
    }

    const updated = {
      ...selectedFundMap,
      [currentCategoryId]: merged,
    };

    isInternalUpdate.current = true;
    setSelectedFundMap(updated);
    onSelectionChange(flattenSelectedFunds(updated));
  };

  const currentSelectedFunds = selectedFundMap[activeCategory?.id] || [];
  const handleDeleteFund = (fundId) => {
    if (!activeCategory) return;

    const updatedCategoryFunds = (
      selectedFundMap[activeCategory.id] || []
    ).filter((fund) => fund.fund_id !== fundId);

    const updatedSelections = {
      ...selectedFundMap,
      [activeCategory.id]: updatedCategoryFunds,
    };

    isInternalUpdate.current = true;
    setSelectedFundMap(updatedSelections);
    onSelectionChange(flattenSelectedFunds(updatedSelections));
  };

  return (
    <div className="row">
      <div className="col-12">
        <div className="e-border-1px-pale-lavender e-border-radius-16 e-p-24px e-bg-ghost-white-dark e-primary-font-poppins w-100">
          <h6 className="e-font-16 e-font-weight-600 e-text-eerie-black pb-0">
            Funds
          </h6>

          <div className="d-flex flex-wrap align-items-start gap-2 pb-4">
            <div className="e-customer-tab d-flex flex-nowrap column-gap-2 e-border-1px-bottom-magnolia-light pt-3">
              <div className="d-flex column-gap-3 w-100">
                {categories.map((category, index) => (
                  <CustomTab
                    key={index}
                    type={1}
                    borderRadius="border-radius-4"
                    background="bg-white"
                    tabData={category}
                    tabIndex={activeCategory?.id}
                    handleChange={(v) => {
                      setActiveCategory({ id: v, label: v });
                      setSearchValue("");
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <CustomSearchInput
                id="fund-search"
                type="text"
                placeholder="Search fund"
                value={searchValue}
                className="w-100 e-search-input-padding"
                handleChange={setSearchValue}
                onKeyPress={setSearchValue}
              />
            </div>
          </div>

          {searchValue && isLoadingFunds && (
            <div className="row pt-3">
              <div className="col-12">
                <Loader width={30} />
              </div>
            </div>
          )}

          {searchValue && !isLoadingFunds && searchedFunds?.length > 0 && (
            <div className="row pt-3">
              <div className="col-12">
                <CustomSelectMenu
                  options={searchedFunds}
                  isMulti
                  value={currentSelectedFunds}
                  onChange={handleSelection}
                />
              </div>
            </div>
          )}

          <div className="d-flex flex-column gap-2 pt-3">
            {currentSelectedFunds.map((fund) => (
              <div
                key={fund.fund_id}
                className="e-bg-magnolia-medium e-border-radius-8 px-3 py-2"
              >
                <div className="row align-items-center justify-content-between">
                  <div className="col-md-1 col-2">
                    <img
                      src={fund.fund_image}
                      alt={fund.fund_name}
                      className="img-fluid"
                    />
                  </div>

                  <div className="col-md-8 col-7">
                    <p className="e-font-14 e-font-weight-400 e-primary-font-poppins m-0">
                      {fund.fund_name}
                    </p>
                  </div>

                  <div className="col-md-3 col-3 d-flex justify-content-end">
                    <PrimaryButton
                      name="Delete"
                      icon="delete"
                      onPress={() => handleDeleteFund(fund.fund_id)}
                      ClassName="bg-transparent px-0 e-text-cg-red e-font-weight-400 e-font-14"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="pt-3">
            <small className="text-muted">
              {currentSelectedFunds.length} / {allowedSelection} Selected
            </small>
          </div>
        </div>
      </div>
    </div>
  );
}
